import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2 } from "lucide-react";
import { SCField, SCModal, SCSection, SCEmpty, useConfirm, fmtDate } from "./Common";
import {
    scAdminListAmcUsers, scAdminCreateAmcUser, scAdminPatchAmcUser, scAdminDeleteAmcUser,
    scAdminListAmcs,
} from "../../../lib/sifcontrolApi";

const emptyCreate = () => ({ email: "", password: "", name: "", amc_id: "", status: "active" });

export default function AmcUsersTab() {
    const [items, setItems] = useState([]);
    const [amcs, setAmcs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const { confirm, dialog } = useConfirm();

    const load = async () => {
        setLoading(true);
        try {
            const [u, a] = await Promise.all([scAdminListAmcUsers(), scAdminListAmcs()]);
            setItems(u.items || []);
            setAmcs(a.items || []);
        } catch { toast.error("Could not load users"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const amcName = useMemo(() => Object.fromEntries(amcs.map((a) => [a.id, a.amc_name])), [amcs]);
    const amcOptions = useMemo(() => [{ value: "", label: "— Select AMC —" }, ...amcs.map((a) => ({ value: a.id, label: a.amc_name }))], [amcs]);

    const openCreate = () => {
        if (amcs.length === 0) return toast.error("Add an AMC first.");
        setModal({ mode: "create", data: { ...emptyCreate(), amc_id: amcs[0].id } });
    };
    const openEdit = (row) => setModal({ mode: "edit", data: { id: row.id, name: row.name || "", amc_id: row.amc_id, status: row.status, password: "" } });

    const save = async () => {
        const d = modal.data;
        setSaving(true);
        try {
            if (modal.mode === "create") {
                if (!d.email || !d.password || d.password.length < 8) return toast.error("Email and 8-char password required");
                if (!d.amc_id) return toast.error("AMC required");
                await scAdminCreateAmcUser(d);
                toast.success("User created");
            } else {
                const patch = { name: d.name, amc_id: d.amc_id, status: d.status };
                if (d.password && d.password.length >= 8) patch.password = d.password;
                await scAdminPatchAmcUser(d.id, patch);
                toast.success("User updated");
            }
            setModal(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const del = async (row) => {
        if (!(await confirm(`Delete user ${row.email}?`))) return;
        try { await scAdminDeleteAmcUser(row.id); toast.success("User deleted"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const setF = (k, v) => setModal({ ...modal, data: { ...modal.data, [k]: v } });

    return (
        <div data-testid="sc-admin-amc-users">
            <SCSection
                title="AMC users"
                subtitle="Accounts that log into the AMC portal to upload SIF data."
                right={<button onClick={openCreate} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-amcuser-add-btn"><Plus size={14} /> Add user</button>}
                testId="sc-amcusers-header"
            />

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <SCEmpty message="No AMC users yet." testId="sc-amcusers-empty" />
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[1.6fr_1fr_120px_120px_100px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>User</div>
                        <div>AMC</div>
                        <div>Status</div>
                        <div>Last login</div>
                        <div className="text-right">Actions</div>
                    </div>
                    <div className="divide-y divide-oth-line/60" data-testid="sc-amcusers-list">
                        {items.map((u) => (
                            <div key={u.id} className="grid grid-cols-[1.6fr_1fr_120px_120px_100px] items-center px-5 py-3.5" data-testid={`sc-amcuser-row-${u.id}`}>
                                <div>
                                    <div className="font-display text-sm font-medium text-oth-ink">{u.email}</div>
                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{u.name || "—"}</div>
                                </div>
                                <div className="text-xs font-body text-oth-ink2 truncate">{amcName[u.amc_id] || "—"}</div>
                                <div className="text-xs font-body">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${u.status === "active" ? "bg-green-100 text-green-800" : "bg-oth-bg-2 text-oth-ink3"}`}>{u.status}</span>
                                </div>
                                <div className="text-[11px] font-body text-oth-ink3">{u.last_login ? fmtDate(u.last_login) : "Never"}</div>
                                <div className="flex items-center justify-end gap-1">
                                    <button onClick={() => openEdit(u)} className="w-8 h-8 rounded-full hover:bg-oth-bg-2 flex items-center justify-center" data-testid={`sc-amcuser-edit-${u.id}`} aria-label="Edit"><Edit3 size={13} /></button>
                                    <button onClick={() => del(u)} className="w-8 h-8 rounded-full hover:bg-red-50 text-oth-ink3 hover:text-red-700 flex items-center justify-center" data-testid={`sc-amcuser-delete-${u.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <SCModal
                open={!!modal}
                title={modal?.mode === "create" ? "Add AMC user" : "Edit AMC user"}
                onClose={() => !saving && setModal(null)}
                testId="sc-amcuser-modal"
                footer={
                    <>
                        <button onClick={() => setModal(null)} disabled={saving} className="btn-pill !text-sm" data-testid="sc-amcuser-modal-cancel">Cancel</button>
                        <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark !text-sm" data-testid="sc-amcuser-modal-save">{saving ? "Saving…" : "Save"}</button>
                    </>
                }
            >
                {modal && (
                    <div>
                        {modal.mode === "create" && (
                            <SCField label="Email" value={modal.data.email} onChange={(v) => setF("email", v)} testId="sc-amcuser-form-email" type="email" required />
                        )}
                        <SCField label="Name" value={modal.data.name} onChange={(v) => setF("name", v)} testId="sc-amcuser-form-name" />
                        <SCField label="AMC" value={modal.data.amc_id} onChange={(v) => setF("amc_id", v)} testId="sc-amcuser-form-amc" type="select" options={amcOptions} required />
                        <SCField
                            label={modal.mode === "create" ? "Password" : "Reset password"}
                            value={modal.data.password}
                            onChange={(v) => setF("password", v)}
                            testId="sc-amcuser-form-password"
                            type="password"
                            hint={modal.mode === "create" ? "Minimum 8 characters" : "Leave blank to keep current password"}
                            required={modal.mode === "create"}
                        />
                        <SCField label="Status" value={modal.data.status} onChange={(v) => setF("status", v)} type="select" options={["active", "inactive"]} testId="sc-amcuser-form-status" />
                    </div>
                )}
            </SCModal>
            {dialog}
        </div>
    );
}
